import debounce from 'lodash.debounce';
import { onMounted, onUnmounted, ref, shallowRef, watch } from 'vue';
import { identity } from '@nzyme/utils/functions/identity.js';
const skipWrite = Symbol('skipWrite');
/**
 * Creates a reactive reference that automatically synchronizes its value with browser storage.
 *
 * Features:
 * - Automatic synchronization with localStorage or sessionStorage
 * - Optional cross-tab synchronization
 * - Configurable serialization (raw, JSON, or custom)
 * - Default values
 * - Deep watching for nested objects
 * - Debounced storage updates
 *
 * @template T The type of value to store
 * @param options Configuration options for the storage ref
 * @returns A StorageRef instance
 *
 * @example
 * ```ts
 * // Simple string storage
 * const name = storageRef({
 *   key: 'user-name',
 *   storage: 'local',
 *   default: () => ''
 * });
 *
 * // JSON storage with cross-tab sync
 * const settings = storageRef({
 *   key: 'app-settings',
 *   storage: 'local',
 *   json: true,
 *   sync: 'always',
 *   default: () => ({ theme: 'light', fontSize: 14 })
 * });
 *
 * // Custom serialization
 * const date = storageRef({
 *   key: 'selected-date',
 *   storage: 'local',
 *   serialize: (date: Date) => date.toISOString(),
 *   deserialize: (str: string) => new Date(str)
 * });
 * ```
 */
export function storageRef(options) {
    const key = options.key;
    const serialize = options.serialize ?? (options.json ? JSON.stringify : identity);
    const deserialize = options.deserialize ?? (options.json ? JSON.parse : identity);
    const storage = getStorage(options.storage);
    const variable = (options.deep ? ref(read()) : shallowRef(read()));
    const watcher = options.debounce ? debounce(write, options.debounce) : write;
    watch(variable, watcher, { deep: options.deep });
    variable.reload = reload;
    variable.startSync = startSync;
    variable.stopSync = stopSync;
    variable.save = save;
    variable.clear = clear;
    if (options.sync && storage) {
        if (options.sync === 'always') {
            startSync();
        }
        else if (options.sync === 'when-mounted') {
            onMounted(startSync);
            onUnmounted(stopSync);
        }
    }
    return variable;
    function reload() {
        const value = read();
        updateNoWrite(value);
        return value;
    }
    function read() {
        if (!storage) {
            return getDefault();
        }
        const item = storage.getItem(key);
        if (!item) {
            return getDefault();
        }
        return deserialize(item);
    }
    function updateNoWrite(value) {
        if (value != null && typeof value === 'object') {
            // Prevent writing back to localStorage
            value[skipWrite] = true;
        }
        variable.value = value;
    }
    function write(value) {
        if (!storage) {
            return;
        }
        if (value == null) {
            storage.removeItem(key);
        }
        else if (value[skipWrite]) {
            delete value[skipWrite];
        }
        else {
            storage.setItem(key, serialize(value));
        }
    }
    function startSync() {
        window.addEventListener('storage', sync);
    }
    function stopSync() {
        window.removeEventListener('storage', sync);
    }
    function sync(event) {
        if (event.storageArea === storage && event.key === key) {
            const value = event.newValue ? deserialize(event.newValue) : getDefault();
            updateNoWrite(value);
        }
    }
    function getDefault() {
        if (options.default) {
            return options.default();
        }
        return null;
    }
    function save() {
        write(variable.value);
    }
    function clear() {
        variable.value = getDefault();
    }
}
function getStorage(storage) {
    if (storage === 'session' && typeof sessionStorage !== 'undefined') {
        return sessionStorage;
    }
    else if (typeof localStorage !== 'undefined') {
        return localStorage;
    }
    return null;
}
//# sourceMappingURL=storageRef.js.map